import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Users,
    MessageSquare,
    Calendar,
    UserPlus,
    Settings as SettingsIcon,
    Bell,
    Search,
    LogOut,
    ShieldCheck,
    ShieldAlert,
    UserCog,
    DollarSign,
    TrendingUp,
    BarChart3,
    Activity,
    Mountain,
    Newspaper,
    Menu,
    X,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import BrandLogo from "@/components/BrandLogo";
import { adminApi } from "@/lib/adminApi";
import SlaTimer from "./SlaTimer";
import { DialerProvider } from "@/components/dialer/DialerProvider";
import DialerPanel from "@/components/dialer/DialerPanel";
import { DialerEdgeHandle } from "@/components/dialer/DialerToggle";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { relativeTime, absoluteTitle } from "@/lib/relativeTime";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

const ADMINS = ["master_admin", "admin"];
const STAFF = ["master_admin", "admin", "agent"];
const NAV = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, roles: ADMINS, end: true },
    { to: "/admin/leads", label: "Leads", icon: Users, roles: ADMINS },
    { to: "/admin/conversations", label: "Jarvis Conversations", icon: MessageSquare, roles: ADMINS },
    { to: "/admin/compliance", label: "Jarvis Compliance", icon: ShieldAlert, roles: ADMINS },
    { to: "/admin/booking", label: "Booking", icon: Calendar, roles: ADMINS },
    { to: "/admin/recruiting", label: "Recruiting", icon: UserPlus, roles: ADMINS },
    { to: "/admin/team", label: "Team", icon: UserCog, roles: ADMINS },
    { to: "/admin/my-team", label: "My Team", icon: Users, roles: ADMINS },
    { to: "/admin/policies", label: "Policies", icon: DollarSign, roles: ADMINS, agentLabel: "My Policies", agentRoles: ["agent"] },
    { to: "/admin/reports/policies-by-agent", label: "Policies by Agent", icon: BarChart3, roles: ADMINS, group: "Reports" },
    { to: "/admin/reports/funnel", label: "Conversion Funnel", icon: BarChart3, roles: ADMINS, group: "Reports" },
    { to: "/admin/activity-log", label: "Activity Log", icon: Activity, roles: ADMINS },
    { to: "/admin/users", label: "Users & Invites", icon: UserCog, roles: ADMINS },
    { to: "/admin/blog", label: "Blog", icon: Newspaper, roles: ADMINS },
    { to: "/admin/settings", label: "Settings", icon: SettingsIcon, roles: ADMINS },
    { to: "/my", label: "My Pipeline", icon: Mountain, roles: ["master_admin", "admin", "agent"] },
];

export default function AdminShell() {
    const { user, signOut } = useAuth();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    // Close the mobile nav whenever the route changes.
    useEffect(() => {
        setMobileNavOpen(false);
    }, [pathname]);

    const visibleNav = NAV.filter((n) => {
        if (n.roles.includes(user?.role)) return true;
        if (n.agentRoles && n.agentRoles.includes(user?.role)) return true;
        return false;
    }).map((n) => {
        // Agents see "My Policies" label for /admin/policies
        if (user?.role === "agent" && n.to === "/admin/policies") {
            return { ...n, label: n.agentLabel || n.label };
        }
        return n;
    });

    const [alerts, setAlerts] = useState([]);
    const [paymentAlerts, setPaymentAlerts] = useState([]);
    // Phase 11 C — server-computed unread counts (per-user watermarks).
    // Overrides the raw list length so opening either bell zeroes its badge.
    const [unread, setUnread] = useState({ hot: 0, lead: 0, payment: 0 });
    const [search, setSearch] = useState("");
    const [searchResults, setSearchResults] = useState(null);
    const [searchOpen, setSearchOpen] = useState(false);

    useEffect(() => {
        let alive = true;
        const load = () => {
            adminApi
                .hotAlerts({ status: "open", limit: 5 })
                .then((rows) => {
                    if (alive) setAlerts(rows);
                })
                .catch(() => {});
            adminApi
                .paymentAlerts({ open_only: true })
                .then((rows) => {
                    if (alive) setPaymentAlerts(rows || []);
                })
                .catch(() => {});
            adminApi
                .notificationCounts()
                .then((c) => {
                    if (alive) setUnread({
                        hot: c?.hot || 0,
                        lead: c?.lead || 0,
                        payment: c?.payment || 0,
                    });
                })
                .catch(() => {});
        };
        load();
        const t = setInterval(load, 15000);
        return () => {
            alive = false;
            clearInterval(t);
        };
    }, []);

    // Phase 11 C — mark-read on bell open. Called from BOTH bells so opening
    // either one clears both badges (matches spec: "opening the bell marks
    // all unread as read"). Optimistic UI + confirmed server response.
    const markAllRead = async () => {
        if (unread.hot === 0 && unread.lead === 0 && unread.payment === 0) return;
        setUnread({ hot: 0, lead: 0, payment: 0 });
        try {
            await adminApi.markAllNotificationsRead();
        } catch {
            // On failure, refresh counts so we don't leave a stale zero.
            try {
                const c = await adminApi.notificationCounts();
                setUnread({
                    hot: c?.hot || 0,
                    lead: c?.lead || 0,
                    payment: c?.payment || 0,
                });
            } catch { /* silent */ }
        }
    };

    const ackPaymentAlert = async (id) => {
        try {
            await adminApi.acknowledgePaymentAlert(id);
            setPaymentAlerts((prev) => prev.filter((a) => a.id !== id));
        } catch {
            /* silent */
        }
    };

    // Keyboard shortcut for search
    useEffect(() => {
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                document.getElementById("admin-search")?.focus();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []);

    useEffect(() => {
        if (!search.trim()) {
            setSearchResults(null);
            setSearchOpen(false);
            return;
        }
        const t = setTimeout(() => {
            adminApi
                .search(search.trim())
                .then((r) => {
                    setSearchResults(r.leads || []);
                    setSearchOpen(true);
                })
                .catch(() => setSearchResults([]));
        }, 180);
        return () => clearTimeout(t);
    }, [search]);

    return (
        <DialerProvider>
        <div className="flex min-h-screen bg-mm-bg" data-testid="admin-shell">
            <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-mm-line bg-white">
                <div className="px-5 py-5 border-b border-mm-line">
                    <BrandLogo size={28} />
                </div>
                <nav className="flex-1 px-2 py-4 space-y-0.5" aria-label="Admin primary">
                    {visibleNav.map((n) => {
                        const Icon = n.icon;
                        return (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.end}
                                className={({ isActive }) =>
                                    `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${
                                        isActive
                                            ? "bg-mm-navy text-white"
                                            : "text-mm-ink hover:bg-mm-bg"
                                    }`
                                }
                                data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <Icon size={15} />
                                {n.label}
                            </NavLink>
                        );
                    })}
                </nav>
                <div className="border-t border-mm-line px-3 py-3 text-xs">
                    <div className="flex items-center gap-2 text-mm-muted">
                        {user?.role === "admin" ? (
                            <ShieldCheck size={12} className="text-emerald-600" />
                        ) : (
                            <ShieldAlert size={12} className="text-indigo-600" />
                        )}
                        <span className="uppercase tracking-widest">{user?.role}</span>
                    </div>
                    <div className="mt-1 truncate text-mm-ink">{user?.email}</div>
                </div>
            </aside>

            <div className="flex min-w-0 flex-1 flex-col">
                <header
                    className="sticky top-0 z-30 flex items-center gap-2 border-b border-mm-line bg-white/85 px-3 py-3 backdrop-blur sm:gap-3 sm:px-4"
                    data-testid="admin-topbar"
                >
                    <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
                        <SheetTrigger asChild>
                            <button
                                type="button"
                                className="inline-flex md:hidden h-11 w-11 shrink-0 items-center justify-center rounded-full border border-mm-line bg-white text-mm-navy transition-colors hover:bg-mm-bg"
                                aria-label="Open admin menu"
                                data-testid="admin-mobile-nav-toggle"
                            >
                                <Menu size={20} />
                            </button>
                        </SheetTrigger>
                        <SheetContent
                            side="left"
                            className="w-[86vw] max-w-[320px] bg-white p-0"
                            data-testid="admin-mobile-nav-drawer"
                        >
                            <SheetTitle className="sr-only">Admin navigation</SheetTitle>
                            <div className="flex items-center justify-between border-b border-mm-line px-5 py-4">
                                <BrandLogo size={26} />
                                <button
                                    type="button"
                                    onClick={() => setMobileNavOpen(false)}
                                    className="inline-flex h-11 w-11 items-center justify-center rounded-full text-mm-navy hover:bg-mm-bg"
                                    aria-label="Close admin menu"
                                    data-testid="admin-mobile-nav-close"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                            <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5" aria-label="Admin mobile">
                                {visibleNav.map((n) => {
                                    const Icon = n.icon;
                                    return (
                                        <NavLink
                                            key={n.to}
                                            to={n.to}
                                            end={n.end}
                                            onClick={() => setMobileNavOpen(false)}
                                            className={({ isActive }) =>
                                                `flex items-center gap-2.5 rounded-lg px-3 py-3 text-sm transition-colors ${
                                                    isActive
                                                        ? "bg-mm-navy text-white"
                                                        : "text-mm-ink hover:bg-mm-bg"
                                                }`
                                            }
                                            data-testid={`admin-mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        >
                                            <Icon size={16} />
                                            {n.label}
                                        </NavLink>
                                    );
                                })}
                            </nav>
                            <div className="border-t border-mm-line px-4 py-3 text-xs">
                                <div className="flex items-center gap-2 text-mm-muted">
                                    {user?.role === "admin" ? (
                                        <ShieldCheck size={12} className="text-emerald-600" />
                                    ) : (
                                        <ShieldAlert size={12} className="text-indigo-600" />
                                    )}
                                    <span className="uppercase tracking-widest">{user?.role}</span>
                                </div>
                                <div className="mt-1 truncate text-mm-ink">{user?.email}</div>
                                <button
                                    onClick={async () => {
                                        setMobileNavOpen(false);
                                        await signOut();
                                        navigate("/login");
                                    }}
                                    className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-full border border-mm-line bg-white px-4 py-2.5 text-sm font-medium text-mm-navy hover:bg-mm-bg"
                                    data-testid="admin-mobile-signout"
                                >
                                    <LogOut size={14} />
                                    Sign out
                                </button>
                            </div>
                        </SheetContent>
                    </Sheet>

                    <div className="md:hidden">
                        <BrandLogo size={24} withWordmark={false} />
                    </div>

                    <div className="relative hidden flex-1 sm:block sm:max-w-md">
                        <Search
                            size={14}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-mm-muted"
                        />
                        <Input
                            id="admin-search"
                            placeholder="Search leads — name, email, phone (⌘K)"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onFocus={() => search && setSearchOpen(true)}
                            className="pl-8"
                            data-testid="admin-global-search"
                        />
                        {searchOpen && searchResults && (
                            <div className="absolute left-0 right-0 top-full mt-1 rounded-xl border border-mm-line bg-white shadow-card" data-testid="admin-search-results">
                                {searchResults.length === 0 ? (
                                    <div className="p-4 text-sm text-mm-muted">No results.</div>
                                ) : (
                                    <ul>
                                        {searchResults.map((l) => (
                                            <li key={l.id}>
                                                <button
                                                    onClick={() => {
                                                        setSearchOpen(false);
                                                        setSearch("");
                                                        navigate(`/admin/leads?open=${l.id}`);
                                                    }}
                                                    className="flex w-full items-center justify-between px-4 py-2 text-left text-sm hover:bg-mm-bg"
                                                >
                                                    <span className="truncate">
                                                        <strong>{l.name || "Unnamed"}</strong>{" "}
                                                        <span className="text-mm-muted">
                                                            {l.email || l.phone}
                                                        </span>
                                                    </span>
                                                    <span className="ml-3 shrink-0 text-[11px] text-mm-muted">
                                                        {l.status}
                                                    </span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex-1 sm:hidden" />

                    <Popover onOpenChange={(open) => { if (open) markAllRead(); }}>
                        <PopoverTrigger asChild>
                            <button
                                className="relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-mm-line bg-white text-mm-navy hover:bg-mm-bg sm:h-9 sm:w-9"
                                aria-label="Hot lead alerts"
                                data-testid="admin-hot-bell"
                            >
                                <Bell size={16} />
                                {unread.hot > 0 && (
                                    <span
                                        className="absolute -right-1 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white"
                                        data-testid="admin-hot-bell-badge"
                                    >
                                        {unread.hot}
                                    </span>
                                )}
                            </button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-[92vw] max-w-sm p-0 sm:w-80" data-testid="admin-hot-popover">
                            <div className="border-b border-mm-line px-4 py-3">
                                <div className="mm-eyebrow">Hot alerts</div>
                                <div className="text-xs text-mm-muted">Open, ranked by recency</div>
                            </div>
                            <div className="max-h-80 overflow-y-auto">
                                {alerts.length === 0 ? (
                                    <div className="p-4 text-sm text-mm-muted">No open hot leads.</div>
                                ) : (
                                    alerts.map((a) => (
                                        <button
                                            key={a.id}
                                            onClick={() =>
                                                navigate(
                                                    a.lead_id
                                                        ? `/admin/leads/${a.lead_id}/vip`
                                                        : `/admin/leads`,
                                                )
                                            }
                                            className="flex min-h-[56px] w-full items-start justify-between gap-3 border-b border-mm-line px-4 py-3 text-left hover:bg-mm-bg"
                                            data-testid={`admin-hot-row-${a.id}`}
                                        >
                                            <div className="min-w-0">
                                                <div className="truncate text-sm font-medium text-mm-ink">
                                                    {a.name || "Unnamed"}
                                                </div>
                                                <div className="truncate text-xs text-mm-muted">
                                                    {a.product_interest || "—"} · {a.source}
                                                </div>
                                                <div
                                                    className="mt-0.5 text-[11px] text-mm-muted"
                                                    title={absoluteTitle(a.created_at)}
                                                    data-testid={`admin-hot-row-${a.id}-time`}
                                                >
                                                    {relativeTime(a.created_at)}
                                                </div>
                                            </div>
                                            <SlaTimer createdAt={a.created_at} />
                                        </button>
                                    ))
                                )}
                            </div>
                        </PopoverContent>
                    </Popover>

                    {/* Phase 15 — Normal new-lead bell (non-hot). */}
                    <button
                        onClick={() => {
                            markAllRead();
                            navigate("/admin/leads?status=New");
                        }}
                        className="relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-mm-line bg-white text-mm-navy hover:bg-mm-bg sm:h-9 sm:w-9"
                        aria-label="New leads"
                        title="New leads"
                        data-testid="admin-lead-bell"
                    >
                        <UserPlus size={16} />
                        {unread.lead > 0 && (
                            <span
                                className="absolute -right-1 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-mm-blue px-1 text-[10px] font-bold text-white"
                                data-testid="admin-lead-bell-badge"
                            >
                                {unread.lead}
                            </span>
                        )}
                    </button>

                    <Popover onOpenChange={(open) => { if (open) markAllRead(); }}>
                        <PopoverTrigger asChild>
                            <button
                                className="relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-mm-line bg-white text-mm-navy hover:bg-mm-bg sm:h-9 sm:w-9"
                                aria-label="Payment alerts"
                                data-testid="admin-payment-bell"
                            >
                                <DollarSign size={16} />
                                {unread.payment > 0 && (
                                    <span
                                        className="absolute -right-1 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-600 px-1 text-[10px] font-bold text-white"
                                        data-testid="admin-payment-bell-badge"
                                    >
                                        {unread.payment}
                                    </span>
                                )}
                            </button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-[92vw] max-w-sm p-0 sm:w-80" data-testid="admin-payment-popover">
                            <div className="border-b border-mm-line px-4 py-3">
                                <div className="mm-eyebrow">Payment alerts</div>
                                <div className="text-xs text-mm-muted">Missed drafts awaiting follow-up</div>
                            </div>
                            <div className="max-h-80 overflow-y-auto">
                                {paymentAlerts.length === 0 ? (
                                    <div className="p-4 text-sm text-mm-muted">No open payment alerts.</div>
                                ) : (
                                    paymentAlerts.slice(0, 5).map((a) => (
                                        <div
                                            key={a.id}
                                            className="flex min-h-[56px] items-start justify-between gap-2 border-b border-mm-line px-4 py-3"
                                            data-testid={`payment-alert-row-${a.id}`}
                                        >
                                            <button
                                                onClick={() => navigate(`/admin/policies?payment_status=missed`)}
                                                className="flex-1 min-w-0 text-left"
                                            >
                                                <div className="truncate text-sm font-medium text-mm-ink">
                                                    {a.lead_name || "Unnamed"}
                                                </div>
                                                <div className="truncate text-xs text-mm-muted">
                                                    {a.carrier || "—"} · draft {a.draft_date_at_trigger || "—"}
                                                </div>
                                                <div
                                                    className="mt-0.5 text-[11px] text-mm-muted"
                                                    title={absoluteTitle(a.triggered_at)}
                                                    data-testid={`payment-alert-row-${a.id}-time`}
                                                >
                                                    {relativeTime(a.triggered_at)}
                                                </div>
                                            </button>
                                            <button
                                                onClick={() => ackPaymentAlert(a.id)}
                                                className="shrink-0 rounded-full border border-mm-line bg-white px-3 py-2 text-xs font-medium text-mm-navy hover:bg-mm-bg sm:px-2 sm:py-0.5 sm:text-[10px]"
                                                data-testid={`payment-alert-ack-${a.id}`}
                                            >
                                                Ack
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                            <div className="border-t border-mm-line bg-mm-bg/40 px-4 py-2 text-[10px] italic text-mm-muted">
                                Moving Mountains does not collect or process payments.
                            </div>
                        </PopoverContent>
                    </Popover>

                    <button
                        onClick={async () => {
                            await signOut();
                            navigate("/login");
                        }}
                        className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-mm-navy hover:text-mm-blue"
                        data-testid="admin-signout"
                    >
                        <LogOut size={14} />
                        Sign out
                    </button>
                </header>

                <main className="min-w-0 flex-1 px-3 py-5 sm:px-6 sm:py-6 lg:px-8" data-testid="admin-main">
                    <Outlet />
                </main>
            </div>
            <DialerPanel />
            <DialerEdgeHandle />
        </div>
        </DialerProvider>
    );
}
